import { FC, useEffect, useState } from "react";

import Table from "@components/Table/index";
import CustomModal from "@components/customModel";
import { IModal, IVersion } from "@types";
import { getVersionsColumns } from "../config";
import {
  ErrorServices,
  useFetchJobGradeVersionsQuery,
  useLazyDownloadJobGradesQuery,
  useUpdateJobGradeVersionMutation,
} from "@services";
import { useTypedSelector } from "@hooks";

interface IGradeSetupVersions extends IModal {}

const GradeSetupVersions: FC<IGradeSetupVersions> = ({
  isVisible,
  setIsVisible,
}) => {
  const { company } = useTypedSelector(state => state.auth.user!);
  const { id: company_id } = company!;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [versionId, setVersionId] = useState<number | null>(null);
  const [pendingVersionId, setPendingVersionId] = useState<number | null>(null);

  const { data: versions, isLoading } = useFetchJobGradeVersionsQuery({
    page,
    pageSize,
    company_id,
  });

  const [updateJobGradeVersion] = useUpdateJobGradeVersionMutation();

  const [
    downloadJobGrades,
    {
      isLoading: isDownloadLoading,
      isFetching: isDownloadFetching,
      originalArgs: downloadArgs,
    },
  ] = useLazyDownloadJobGradesQuery();

  const isDownloading = isDownloadLoading || isDownloadFetching;

  const { data, pagination } = versions || {};

  useEffect(() => {
    if (!data?.length) return;

    const activeVersion = data.find(
      (version: IVersion) => version.is_active === "TRUE"
    );

    setVersionId(activeVersion?.id ?? null);
  }, [data]);

  const handleActive = async (id: number) => {
    const previousVersionId = versionId;

    // Optimistic UI:
    // move the switch immediately, but show loading until API confirms.
    setVersionId(id);
    setPendingVersionId(id);

    try {
      await updateJobGradeVersion({
        id,
        company_id,
      }).unwrap();

      // API confirmed the change.
      setPendingVersionId(null);
    } catch (error) {
      // API rejected the change, so restore previous active version.
      setVersionId(previousVersionId);
      setPendingVersionId(null);

      ErrorServices(error);
    }
  };

  const versionsColumns = getVersionsColumns({
    handleActive,
    versionId,
    pendingVersionId,
    company_id,
    downloadJobGrades,
    downloadArgs,
    isDownloading,
  });

  return (
    <CustomModal
      isVisible={isVisible}
      title="Previous Versions of Job Grades"
      onClose={() => setIsVisible(false)}
      footer={null}
      width="w-3/4"
      height="80vh"
      bg="bg-white"
    >
      <Table
        isLoading={isLoading}
        data={data}
        columns={versionsColumns}
        pagination
        customStyles="mx-4"
        count={pagination?.count}
        onChangePage={setPage}
        onPageSizeChange={setPageSize}
        page={page}
      />
    </CustomModal>
  );
};

export default GradeSetupVersions;